import useAppContext from 'flow/hooks/useAppContext';
import TranscriptionSummaryMarkdown from './TranscriptionSummaryMarkdown';

interface Props {
    summary?: string;
    animate?: boolean;
}

export const REVEAL_CHARS_PER_TICK = 5;
export const REVEAL_INTERVAL_MS = 16;

// However long the summary, the reveal is over by here. Without a ceiling the
// pace is fixed and the duration is not: at five characters a tick a long
// summary takes minutes, and the card sits half-written for all of it.
export const REVEAL_MAX_DURATION_MS = 4000;

const REVEAL_MAX_TICKS = REVEAL_MAX_DURATION_MS / REVEAL_INTERVAL_MS;

/**
 * How many characters share a tick: the base pace for a summary short enough to
 * finish in time, and as many more as it takes for one that isn't.
 */
export const revealCharsPerTick = (summaryLength: number) =>
    Math.max(
        REVEAL_CHARS_PER_TICK,
        Math.ceil(summaryLength / REVEAL_MAX_TICKS),
    );

/**
 * How long the reveal runs. Read by whatever follows the summary on screen, so
 * it can wait for it rather than guess.
 */
export const revealDurationMs = (summaryLength: number) =>
    Math.ceil(summaryLength / revealCharsPerTick(summaryLength)) *
    REVEAL_INTERVAL_MS;

const TranscriptionSummary = ({ summary, animate }: Props) => {
    const { i18next } = useAppContext();

    if (!summary) {
        return (
            <div className="text-sm text-gray-400 italic antialiased">
                {i18next.t('ai.summary.no_summary_found')}
            </div>
        );
    }

    // The whole summary is here on the first frame — nothing streams, the
    // typewriter is an effect over text we already hold. So it is parsed once,
    // in full, and the reveal is CSS moving over the result. Rendering a prefix
    // would mean parsing a partial markdown document on every tick, and partial
    // markdown is not markdown: a half-revealed `**bold**` shows its asterisks.
    return (
        <div className="text-sm antialiased">
            <TranscriptionSummaryMarkdown
                markdown={summary}
                revealCharsPerTick={
                    animate ? revealCharsPerTick(summary.length) : undefined
                }
            />
        </div>
    );
};

export default TranscriptionSummary;
